import { PIPELINE_MIGRATE as CONTINUITY_PIPELINE_MIGRATE } from '../../content/productContinuityPage';
import { SCRIPT_FAQ } from '../../content/productScriptPage';
import { PageFaqSection } from '../ds';
import { CDN } from '../../lib/cdn';
import { ProductFinalCta } from './ProductFinalCta';
import { ProductHeroHeader } from './ProductHeroHeader';
import { ProductMigrate } from './ProductMigrate';

export function ProductScriptSections() {
  return (
    <div className="product-page">
      <ProductHeroHeader
        label="Script to Video"
        title={
          <>
            Script to Video, the Way
            <br />
            a <em>Director Reads It</em>
          </>
        }
        sub="Induce reads your screenplay as cause and effect, plans every shot from the story’s logic, and verifies each one before it reaches your cut."
        note="FDX · PDF · Fountain · Paste — script-first pipeline"
        still={{
          kind: 'script_to_cut',
          slug: 'SCRIPT → SHOT',
          scene: 'SCRIPT / 01',
          genre: 'BREAKDOWN',
          sid: 'script-hero',
          videoSrc: CDN.videos.scriptEditor,
          alt: 'Script editor demo — screenplay parsed into shots in Induce',
        }}
      />

      <section className="answer band-light" aria-labelledby="se-aeo-h" id="how-it-works">
        <div className="container">
          <div className="content-measure">
            <div className="tag">- How it works</div>
            <div className="answer-body">
              <h2 id="se-aeo-h" className="a-lead">
                <b>
                  Turning a script into video isn&apos;t a translation problem. It&apos;s a reasoning problem.
                </b>
              </h2>
              <p className="a-body">
                {/* A screenplay encodes causality and intent, not just dialogue — and most AI tools discard it. */}
                Rhapsody, Induce&apos;s causal reasoning engine, reads a script the way a director does: it
                holds the whole story in memory and checks every shot against it.
              </p>
              <div className="a-stats reveal">
                <div className="a-stat">
                  <b>80%</b>
                  <span>Less prompting</span>
                </div>
                <div className="a-stat">
                  <b>4</b>
                  <span>Supported ingestion formats</span>
                </div>
                <div className="a-stat">
                  <b>L/L</b>
                  <span>Every line has its own shot</span>
                </div>
                <div className="a-stat">
                  <b>&lt;3 minutes</b>
                  <span>Full-script breakdown time</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="band-dark" id="formats">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- Supported formats</span>
            <h2>
              Upload the file you&apos;re <em>already writing.</em>
            </h2>
            <p className="sub">
              Every major screenwriting format works as-is. No conversion, no reformatting.
            </p>
          </div>
          <div className="format-list reveal">
            {[
              ['.fdx', 'Final Draft', 'Full document structure - slug lines, dual dialogue, annotations, production notes'],
              ['.pdf', 'PDF Screenplay', 'Standard formatting detected - action, character, and dialogue parsed automatically'],
              ['.fountain', 'Fountain Plain Text', 'Full Fountain spec - headings, action, dialogue, lyrics, transitions'],
              ['Paste', 'Plain Text / Draft', 'Paste formatted screenplay text - Induce auto-detects the structure'],
            ].map(([ext, name, sub]) => (
              <div key={ext} className="format-item">
                <div>
                  <div className="f-ext">{ext}</div>
                </div>
                <div>
                  <div className="f-name">{name}</div>
                  <div className="f-sub">{sub}</div>
                </div>
                <div className="f-check">Supported</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="band-light" id="breakdown">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- The architecture behind this</span>
            <h2>
              Script → Plan → Generate → <em>Verify.</em>
            </h2>
            <p className="sub">Four stages of Rhapsody’s closed loop, each feeding the next.</p>
          </div>
          <div className="step-flow reveal">
            {[
              [
                '01',
                'Ingest: structure detected, causality extracted',
                'Induce parses your format: slug lines become scene anchors, characters a provisional cast. Underneath, Rhapsody extracts the causal chain — who wants what, and why.',
                'Ingestion',
              ],
              [
                '02',
                'Scene breakdown: every beat, with its story state',
                'Each scene becomes a node — location, time-of-day, characters, emotional register — carrying the story state at that moment: what the audience knows, what must hold.',
                'Scene Map',
              ],
              [
                '03',
                'Shot plan: line by line, nothing skipped',
                'Action and dialogue become individual shots with camera, lens, and blocking. Each spec carries its story context, so generation never renders blind.',
                'Shot Plan',
              ],
              [
                '04',
                'Generate and verify: only passing shots enter the cut',
                'Each shot routes to the best-fit model, then the Coherence Verifier checks it against story causality and film-grammar rules. Failures re-plan automatically.',
                'Verify',
              ],
            ].map(([n, title, body, tag]) => (
              <div key={n} className="step-item">
                <div className="step-n">{n}</div>
                <div className="step-body">
                  <h3>{title}</h3>
                  <p>{body}</p>
                </div>
                <div className="step-tag">{tag}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="band-dark" id="compare">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- Script-first vs. prompt-first</span>
            <h2>The difference a screenplay makes.</h2>
            <p className="sub">
              Every prompt-first tool puts the burden of story structure on you — and checks nothing.
            </p>
          </div>
          <div className="cap-grid reveal">
            <div className="cap-item">
              <div className="kicker">Prompt-first tools (Runway, Kling, Veo direct)</div>
              <h3>One clip. One prompt. No memory, no check.</h3>
              <p>
                Write a prompt, get a clip, then re-describe the same character, location, and tone for the
                next one. Nothing carries forward, and nothing flags a broken story.
              </p>
            </div>
            <div className="cap-item">
              <div className="kicker">Script-first (Induce)</div>
              <h3>One upload. Every shot planned, generated, verified.</h3>
              <p>
                Upload once. Rhapsody casts the characters, maps the locations, plans the coverage — and
                verifies every shot against that same source of truth.
              </p>
            </div>
          </div>
        </div>
      </section>

      <PageFaqSection
        eyebrow="- Script to video, answered"
        title={
          <>
            Script to video, <em>answered.</em>
          </>
        }
        items={SCRIPT_FAQ}
      />

      <ProductMigrate {...CONTINUITY_PIPELINE_MIGRATE} tone="dark" />

      <ProductFinalCta
        tone="light"
        eyebrow="- Your script is ready"
        title={
          <>
            Turn it into a <em>cut.</em>
          </>
        }
        body="Induce does the rest — breakdown, memory, verification, and a first cut that's ready to refine."
        ghostLabel="Talk to Sales"
        ghostIsSales
        note="Free beta · No credit card · Commercial rights included"
      />
    </div>
  );
}
